import { Fragment } from "react";
import { useForm } from "react-hook-form";
import type { Path } from "react-hook-form";
import {
  Alert,
  Checkbox,
  FormModal,
  FormSectionLabel,
  Input,
  Select,
  Textarea,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { isPhoneNumber } from "../../lib/sms";
import {
  useCreateSender,
  useUpdateSender,
  type Sender,
  type SenderCreate,
  type SenderUpdate,
  type TLSMode,
} from "../../lib/queries/senders";

interface SenderFormValues {
  key: string;
  name: string;
  channel: "email" | "sms";
  /** "dummy" never delivers; "real" is SMTP for e-mail, a phone for SMS. */
  delivery: "real" | "dummy";
  from_address: string;
  from_name: string;
  unsubscribe_url: string;
  host: string;
  port: string;
  tls_mode: TLSMode;
  username: string;
  secret: string;
  copy_to: string;
  /** E-mail: addresses, domains and *.domains, one per line; empty means anyone. */
  allowed_recipients: string;
  max_in_flight: string;
  rate_per_minute: string;
  active: boolean;
  /** SMS: comma-separated E.164 prefixes; empty means anywhere. */
  allowed_prefixes: string;
  /** SMS: refuse a message billed as more segments than this. */
  max_segments: string;
}

const TLS_OPTIONS: { value: TLSMode; label: string }[] = [
  { value: "implicit", label: "Implicit TLS (usually port 465)" },
  { value: "starttls", label: "STARTTLS (usually port 587)" },
  { value: "none", label: "No encryption" },
];

function defaultsFrom(sender?: Sender): SenderFormValues {
  return {
    key: sender?.key ?? "",
    name: sender?.name ?? "",
    channel: sender?.channel === "sms" ? "sms" : "email",
    delivery: sender?.type === "dummy" ? "dummy" : "real",
    from_address: sender?.from_address ?? "",
    from_name: sender?.from_name ?? "",
    unsubscribe_url: sender?.unsubscribe_url ?? "",
    host: sender?.smtp?.host ?? "",
    port: sender?.smtp?.port ? String(sender.smtp.port) : "",
    tls_mode: sender?.smtp?.tls_mode ?? "starttls",
    username: sender?.smtp?.username ?? "",
    secret: "",
    copy_to: sender?.smtp?.copy_to ?? "",
    allowed_recipients: (sender?.smtp?.allowed_recipients ?? []).join("\n"),
    max_in_flight: sender?.max_in_flight ? String(sender.max_in_flight) : "1",
    rate_per_minute:
      sender?.rate_per_minute != null ? String(sender.rate_per_minute) : "",
    active: sender?.active ?? true,
    allowed_prefixes: (sender?.sms?.allowed_prefixes ?? []).join(", "),
    max_segments: sender?.sms?.max_segments
      ? String(sender.sms.max_segments)
      : "3",
  };
}

/** Maps snake_case + nested smtp.* backend field paths onto flat RHF names. */
function mapField(backendField: string): Path<SenderFormValues> {
  const flat = backendField.replace(/^smtp\./, "");
  const known: Record<string, Path<SenderFormValues>> = {
    key: "key",
    name: "name",
    from_address: "from_address",
    from_name: "from_name",
    unsubscribe_url: "unsubscribe_url",
    host: "host",
    port: "port",
    tls_mode: "tls_mode",
    username: "username",
    secret: "secret",
    copy_to: "copy_to",
    allowed_recipients: "allowed_recipients",
    max_in_flight: "max_in_flight",
    rate_per_minute: "rate_per_minute",
    active: "active",
    allowed_prefixes: "allowed_prefixes",
    max_segments: "max_segments",
  };
  return known[flat] ?? ("name" as Path<SenderFormValues>);
}

/** Mirrors the server check: empty, or an https URL with a host and no credentials,
 * fragment or characters that would break the List-Unsubscribe header; ≤ 512 chars. */
function isUnsubscribeURL(value: string): boolean {
  const v = value.trim();
  if (!v) return true;
  try {
    const url = new URL(v);
    return (
      url.protocol === "https:" &&
      url.hostname !== "" &&
      url.username === "" &&
      url.password === "" &&
      url.hash === "" &&
      !/[\s<>",]/.test(v) &&
      v.length <= 512
    );
  } catch {
    return false;
  }
}

export function SenderFormModal({
  initial,
  onClose,
}: {
  initial?: Sender;
  onClose: () => void;
}) {
  const isEdit = !!initial;
  const create = useCreateSender();
  const update = useUpdateSender();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<SenderFormValues>({
    mode: "onTouched",
    defaultValues: defaultsFrom(initial),
  });
  // An SMS sender is a phone holding a SIM: there is no server to reach and
  // no credential to store, so the SMTP block and the secret are hidden. The
  // channel is fixed once the sender exists.
  const isSMS = watch("channel") === "sms";
  // A dummy sender has no transport either: no SMTP block, no secret, and no
  // limits, since nothing is sent. An SMS dummy keeps the SMS rules, which
  // are checked when a message is enqueued. Fixed once the sender exists.
  const isDummy = watch("delivery") === "dummy";

  async function onSubmit(values: SenderFormValues) {
    const smtp = {
      host: values.host.trim(),
      port: values.port ? Number(values.port) : undefined,
      tls_mode: values.tls_mode,
      username: values.username.trim() || undefined,
      copy_to: values.copy_to.trim() || undefined,
      // Always sent, so clearing the field lifts the limit (an absent list
      // would keep the stored one).
      allowed_recipients: values.allowed_recipients
        .split(/[\s,]+/)
        .filter((r) => r !== ""),
    };
    const sms = {
      allowed_prefixes: values.allowed_prefixes
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p !== ""),
      max_segments: Number(values.max_segments) || undefined,
    };
    try {
      if (isEdit) {
        const input: SenderUpdate = {
          name: values.name.trim(),
          // For SMS this is the pinned number, and "" unpins the sender.
          from_address: values.from_address.trim(),
          ...(isSMS
            ? { sms }
            : {
                from_name: values.from_name.trim(),
                // An empty string reverts to letterman's own unsubscribe page.
                unsubscribe_url: values.unsubscribe_url.trim(),
                ...(isDummy ? {} : { smtp }),
              }),
          max_in_flight: Number(values.max_in_flight) || 1,
          rate_per_minute: values.rate_per_minute
            ? Number(values.rate_per_minute)
            : null,
          active: values.active,
        };
        // "Leave empty to keep current": an omitted secret keeps the stored
        // one; the field is only sent when the admin typed a new value.
        if (values.secret) input.secret = values.secret;
        await update.mutateAsync({ id: initial!.id, input });
        notifySuccess("The sender was updated.");
      } else {
        const input: SenderCreate = {
          key: values.key.trim(),
          name: values.name.trim(),
          channel: values.channel,
          from_address: values.from_address.trim() || undefined,
          ...(isSMS
            ? { type: isDummy ? ("dummy" as const) : ("android_sim" as const), sms }
            : {
                type: isDummy ? ("dummy" as const) : ("smtp" as const),
                from_name: values.from_name.trim() || undefined,
                unsubscribe_url: values.unsubscribe_url.trim() || undefined,
                ...(isDummy
                  ? {}
                  : { smtp, secret: values.secret || undefined }),
              }),
          max_in_flight: Number(values.max_in_flight) || undefined,
          rate_per_minute: values.rate_per_minute
            ? Number(values.rate_per_minute)
            : undefined,
        };
        await create.mutateAsync(input);
        notifySuccess("The sender was created.");
      }
      onClose();
    } catch (err) {
      apiErrorToFormErrors<SenderFormValues>(err, setError, {
        fieldMap: mapField,
        fallbackMessage: "Saving failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title={isEdit ? `Edit: ${initial!.name}` : "New sender"}
      onClose={onClose}
      width={560}
      busy={create.isPending || update.isPending}
      error={rootErrorMessage(errors)}
      onSubmit={handleSubmit(onSubmit)}
    >
      {!isEdit && (
        <Input
          label="Key"
          required
          hint="Immutable identifier (lowercase letters, digits, hyphens) — used in the API."
          autoComplete="off"
          spellCheck={false}
          error={errors.key?.message}
          {...register("key", {
            required: "Required field",
            pattern: {
              value: /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/,
              message:
                "Only lowercase letters, digits and hyphens; must not start or end with a hyphen.",
            },
          })}
        />
      )}
      <Input
        label="Name"
        required
        error={errors.name?.message}
        {...register("name", { required: "Required field" })}
      />
      {!isEdit && (
        <Select
          label="Channel"
          hint="Fixed once the sender exists."
          error={errors.channel?.message}
          {...register("channel")}
        >
          <option value="email">E-mail</option>
          <option value="sms">SMS</option>
        </Select>
      )}
      {!isEdit ? (
        <Select
          label="Delivery"
          hint={
            isDummy
              ? "Messages are marked as test and reported as sent without being delivered — for testing integrations."
              : undefined
          }
          error={errors.delivery?.message}
          {...register("delivery")}
        >
          <option value="real">
            {isSMS
              ? "Real (via an Android phone holding the SIM)"
              : "Real (via SMTP)"}
          </option>
          <option value="dummy">Dummy (never delivers)</option>
        </Select>
      ) : (
        isDummy && (
          <Alert variant="info" title="Dummy sender">
            It never delivers: its messages are marked as test and reported as
            sent.
          </Alert>
        )
      )}
      {isSMS ? (
        <Input
          label="Phone number"
          type="tel"
          placeholder="+420777123456"
          spellCheck={false}
          hint={
            isDummy
              ? "Optional. A dummy sender sends nothing, so the number is only recorded."
              : "Optional, and a pin: set it and only a phone reporting this number sends for this sender; leave it empty and any phone scoped to it may, so a recipient can see a different number each time. letterman cannot choose the number — the SIM does."
          }
          error={errors.from_address?.message}
          {...register("from_address", {
            // react-hook-form merges the rules registered under one name, so
            // the e-mail field's `required` must be undone explicitly when the
            // channel switches to SMS.
            required: false,
            validate: (v) =>
              !v.trim() || isPhoneNumber(v) || "Must be a number in E.164 form",
          })}
        />
      ) : (
        <Fragment key="email-identity">
          <Input
            label="From address"
            type="email"
            required
            placeholder="noreply@example.com"
            error={errors.from_address?.message}
            {...register("from_address", {
              required: "Required field",
              validate: undefined,
            })}
          />
          <Input
            label="From name"
            placeholder="My Company"
            error={errors.from_name?.message}
            {...register("from_name")}
          />
        </Fragment>
      )}

      {/* Keyed: without keys React reuses an SMS field's <input> for the
          e-mail field rendered at the same position, and its value (the
          segment limit) shows up in the unsubscribe URL after a switch. */}
      {isSMS ? (
        <Fragment key="sms-settings">
          <FormSectionLabel>SMS</FormSectionLabel>
          <Input
            label="Allowed prefixes"
            placeholder="+420, +421"
            spellCheck={false}
            hint="Comma-separated E.164 prefixes recipients must start with. Leave empty to allow anywhere — this is the guard against a typo becoming an intercontinental bill."
            error={errors.allowed_prefixes?.message}
            {...register("allowed_prefixes")}
          />
          <Input
            label="Segment limit"
            type="number"
            hint="Refuse a message billed as more segments than this. One segment is 160 characters, or 70 when any character is outside GSM-7."
            error={errors.max_segments?.message}
            {...register("max_segments", {
              validate: (v) =>
                !v || (Number(v) >= 1 && Number(v) <= 10) || "Between 1 and 10",
            })}
          />
        </Fragment>
      ) : (
        <Fragment key="email-settings">
      {!isDummy && (
        <>
      <FormSectionLabel>SMTP server</FormSectionLabel>
      <Input
        label="Host"
        required
        placeholder="smtp.example.com"
        spellCheck={false}
        error={errors.host?.message}
        {...register("host", { required: "Required field" })}
      />
      <Input
        label="Port"
        type="number"
        placeholder="587"
        error={errors.port?.message}
        {...register("port", {
          validate: (v) =>
            !v || (Number(v) >= 1 && Number(v) <= 65535) || "Invalid port",
        })}
      />
      <Select label="Encryption" error={errors.tls_mode?.message} {...register("tls_mode")}>
        {TLS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
      <Input
        label="Username"
        autoComplete="off"
        spellCheck={false}
        error={errors.username?.message}
        {...register("username")}
      />
      <Input
        label="Password (SMTP)"
        type="password"
        autoComplete="new-password"
        placeholder={
          isEdit && initial!.has_secret ? "leave empty to keep current" : undefined
        }
        hint={
          isEdit
            ? "The password is never displayed. Leave empty to keep the current one."
            : undefined
        }
        error={errors.secret?.message}
        {...register("secret")}
      />
      <Input
        label="Blind copy (BCC)"
        type="email"
        hint="Optional — every message from this sender is quietly sent here too."
        error={errors.copy_to?.message}
        {...register("copy_to")}
      />
      <Textarea
        label="Allowed recipients"
        rows={3}
        placeholder={"example.com\n*.example.com\nalice@example.org"}
        spellCheck={false}
        hint="One per line: an address, a domain, or *.domain for its subdomains (which does not include the domain itself — list both if you want both). Anyone else is refused when a message is sent, retried or redelivered — for a staging sender, or an integration you don't trust yet. Leave empty to allow anyone. The BCC above is not checked."
        error={errors.allowed_recipients?.message}
        {...register("allowed_recipients")}
      />
        </>
      )}

      <FormSectionLabel>Unsubscribe</FormSectionLabel>
      <Input
        label="Unsubscribe page URL"
        type="url"
        placeholder="https://example.com/letterman/unsubscribe"
        spellCheck={false}
        hint="Optional. Marketing mail — including the List-Unsubscribe header — then links to your page with ?token=…. The page must redeem the token via the API and accept one-click POSTs. Leave empty to use letterman's own page."
        error={errors.unsubscribe_url?.message}
        {...register("unsubscribe_url", {
          validate: (v) => isUnsubscribeURL(v) || "Must be an https:// address",
        })}
      />
        </Fragment>
      )}

      {!isDummy && (
        <>
      <FormSectionLabel>Limits</FormSectionLabel>
      <Input
        label="Max concurrent sends"
        type="number"
        required
        hint={isSMS ? "How many of this sender's messages may be held at once — roughly, how many phones serve it." : undefined}
        error={errors.max_in_flight?.message}
        {...register("max_in_flight", {
          required: "Required field",
          validate: (v) => Number(v) >= 1 || "Must be at least 1",
        })}
      />
      <Input
        label="Messages per minute limit"
        type="number"
        hint="Leave empty for unlimited sending."
        error={errors.rate_per_minute?.message}
        {...register("rate_per_minute", {
          validate: (v) => !v || Number(v) >= 1 || "Must be at least 1",
        })}
      />
        </>
      )}
      {isEdit && <Checkbox label="Active" {...register("active")} />}
    </FormModal>
  );
}
